// Import learning system
import { recordUnansweredQuestion, recordQuestionFrequency, getSuggestedQuestions } from './learningSystem';

// Comprehensive UniTrade Knowledge Base
export const knowledgeBase = [
  // UniTrade Overview
  {
    category: 'UniTrade Overview',
    questions: [
      'what is unitrade',
      'about unitrade',
      'what does unitrade do',
      'tell me about this app',
      'what is this platform'
    ],
    answer: 'UniTrade is Ghana\'s premier university marketplace connecting students, faculty, and staff across all Ghanaian universities. We facilitate safe buying and selling within university communities, making it easy to find textbooks, electronics, furniture, and more from trusted members of your academic community.\n\n🎓 **Developed by:** Nexus Coders - A talented group of programmers from the University of Energy and Natural Resources (UENR)'
  },

  // UniTrade Developers & Team
  {
    category: 'Development Team',
    questions: [
      'who developed unitrade',
      'who made unitrade',
      'who created unitrade',
      'developers of unitrade',
      'who built unitrade',
      'unitrade developers',
      'nexus coders',
      'who are nexus coders',
      'development team',
      'creators of unitrade',
      'unitrade team',
      'who made this app',
      'who created this platform',
      'uenr developers',
      'university of energy and natural resources',
      'uenr students',
      'sunyani developers',
      'ghana developers',
      'student developers',
      'about the team',
      'development story'
    ],
    answer: '👨‍💻 **UniTrade Development Team:**\n\nUniTrade was proudly developed by **Nexus Coders** - a skilled group of programmers from the **University of Energy and Natural Resources (UENR)**.\n\n🎓 **About Nexus Coders:**\n• Student developers from UENR\n• Passionate about creating solutions for university communities\n• Focused on connecting students across Ghana\n• Committed to building safe and reliable marketplace technology\n\n🏛️ **University of Energy and Natural Resources (UENR):**\n• Located in Sunyani, Ghana\n• One of Ghana\'s leading public universities\n• Known for excellence in technology and innovation\n• Part of the 49+ universities supported by UniTrade\n\n💡 **Mission:** To empower university communities across Ghana with a trusted, secure, and user-friendly marketplace platform that brings students, faculty, and staff together for safe trading.'
  },

  // App Features & Capabilities
  {
    category: 'App Features',
    questions: [
      'what can this app do',
      'app features',
      'what are the features',
      'how does this app work',
      'what can i do here',
      'platform capabilities'
    ],
    answer: 'UniTrade offers comprehensive marketplace features:\n\n🛍️ **Shopping & Buying:**\n• Browse products by category or university\n• Advanced search and filtering\n• Secure checkout and payment\n• Order tracking and history\n• Wishlist and cart management\n\n📦 **Selling & Business:**\n• List products with photos and descriptions\n• Seller dashboard and analytics\n• Inventory management\n• Order fulfillment tools\n• Professional seller verification\n\n💬 **Communication:**\n• Direct messaging with buyers/sellers\n• AI-powered customer support\n• Admin chat support\n• Real-time notifications\n\n👤 **Account Management:**\n• University-verified profiles\n• Role-based access (Buyer/Seller/Admin)\n• Settings and preferences\n• Security and privacy controls'
  },

  // User Roles & Access
  {
    category: 'User Roles',
    questions: [
      'user roles',
      'account types',
      'buyer seller admin',
      'what roles are available',
      'different user types',
      'account permissions'
    ],
    answer: 'UniTrade has three main user roles:\n\n👤 **Buyer Account:**\n• Browse and purchase products\n• Manage cart and wishlist\n• Track orders and delivery\n• Rate and review sellers\n• Access to buyer dashboard\n\n🏪 **Seller Account:**\n• List and manage products\n• Process orders and sales\n• Access seller analytics\n• Manage inventory\n• Professional verification available\n\n⚡ **Admin Account:**\n• Platform management and oversight\n• User verification and support\n• Content moderation\n• System analytics and reporting\n• Seller verification management\n\nYou can upgrade from Buyer to Seller anytime through your profile settings!'
  },

  // Universities & Locations
  {
    category: 'Universities',
    questions: [
      'which universities',
      'supported universities',
      'ghana universities',
      'university list',
      'what schools',
      'available universities'
    ],
    answer: 'UniTrade serves ALL universities in Ghana! We support 49+ institutions including:\n\n🏛️ **Public Universities:**\n• University of Ghana (Legon)\n• KNUST (Kwame Nkrumah University of Science and Technology)\n• University of Cape Coast\n• University of Education, Winneba\n• University for Development Studies\n• And many more!\n\n🏫 **Technical Universities:**\n• Accra Technical University\n• Kumasi Technical University\n• Cape Coast Technical University\n• All 10 technical universities\n\n🎓 **Private Universities:**\n• Ashesi University\n• Central University\n• Valley View University\n• Methodist University College\n• And 20+ private institutions\n\nYou can filter products by your specific university to find items from your campus community!'
  },

  // Product Categories
  {
    category: 'Product Categories',
    questions: [
      'what products do you sell',
      'what items do you have',
      'what do you sell',
      'products available',
      'what can i buy',
      'list of products',
      'categories available',
      'product types'
    ],
    answer: 'UniTrade offers 6 main categories tailored for university life:\n\n📚 **Textbooks & Academic Materials:**\n• Course textbooks and references\n• Study guides and notes\n• Academic journals and papers\n• Research materials\n\n💻 **Electronics & Tech:**\n• Laptops and computers\n• Phones and tablets\n• Calculators and tech accessories\n• Gaming equipment\n\n✏️ **Stationery & Supplies:**\n• Notebooks and writing materials\n• Art and design supplies\n• Office and study accessories\n• Printing and binding materials\n\n🪑 **Furniture & Dorm Items:**\n• Dorm furniture and decor\n• Storage solutions\n• Bedding and linens\n• Kitchen appliances\n\n👕 **Clothing & Fashion:**\n• Casual and formal wear\n• University merchandise\n• Shoes and accessories\n• Seasonal clothing\n\n🍔 **Food & Beverages:**\n• Snacks and beverages\n• Meal deals and catering\n• Local and international foods\n• Healthy options'
  },

  // Account Management
  {
    category: 'Account Management',
    questions: [
      'how do i create an account',
      'sign up',
      'register account',
      'how to join',
      'create profile',
      'registration process'
    ],
    answer: 'Creating your UniTrade account is easy:\n\n📝 **Step 1: Basic Information**\n• Visit our registration page\n• Choose your role (Buyer or Seller)\n• Enter email, password, and basic details\n\n🎓 **Step 2: University Verification**\n• Select your university from our list\n• Provide student/staff ID or email\n• Upload verification documents if required\n\n✉️ **Step 3: Email Verification**\n• Check your email for verification code\n• Enter the 6-digit code to confirm\n• Your account will be activated\n\n👤 **Step 4: Complete Profile**\n• Add profile picture and bio\n• Set your preferences\n• Start buying or selling!\n\n**Note:** University verification helps maintain a trusted community of verified academic members.'
  },
  // Buying & Shopping Process
  {
    category: 'Shopping Process',
    questions: [
      'how to buy',
      'how can i buy',
      'how do i buy',
      'how can i buy a product',
      'how do i buy a product',
      'how to buy a product',
      'how to buy products',
      'how can i purchase',
      'how do i purchase',
      'shopping process',
      'how to purchase',
      'buying guide',
      'checkout process',
      'how to order',
      'how can i order',
      'how do i order',
      'purchase process',
      'buying process'
    ],
    answer: 'Shopping on UniTrade is simple and secure:\n\n🔍 **Step 1: Browse & Search**\n• Use the search bar or browse categories\n• Filter by university, price, or condition\n• View detailed product information\n\n🛒 **Step 2: Add to Cart**\n• Click "Add to Cart" on products you want\n• Review items in your cart\n• Apply any available discounts\n\n💳 **Step 3: Secure Checkout**\n• Enter shipping information\n• Choose payment method\n• Review and confirm your order\n\n📦 **Step 4: Track & Receive**\n• Get order confirmation via email\n• Track your order status\n• Receive your items safely\n\n**Pro Tip:** Add items to your wishlist to save them for later!'
  },

  // Order Management & Tracking
  {
    category: 'Order Management',
    questions: [
      'how to track my order',
      'where is my package',
      'order status',
      'track my order',
      'check order status',
      'when will my order arrive',
      'order history',
      'my orders'
    ],
    answer: 'Managing your orders is easy with UniTrade:\n\n📱 **Order Tracking:**\n• Visit "My Orders" in your account dashboard\n• View real-time order status updates\n• Get estimated delivery dates\n• Receive SMS/email notifications\n\n📋 **Order Statuses:**\n• **Pending:** Order received, awaiting seller confirmation\n• **Confirmed:** Seller has confirmed your order\n• **Processing:** Item being prepared for shipment\n• **Shipped:** Order is on its way to you\n• **Delivered:** Order successfully delivered\n• **Completed:** Transaction finished\n\n🔄 **Order Actions:**\n• Cancel orders (if not yet shipped)\n• Request returns or refunds\n• Rate and review your purchase\n• Contact seller directly\n• Download receipts and invoices'
  },

  // Returns & Refunds Policy
  {
    category: 'Returns & Refunds',
    questions: [
      'how to return an item',
      'return policy',
      'refund process',
      'how do i get a refund',
      'return item',
      'exchange policy',
      'money back guarantee'
    ],
    answer: 'UniTrade\'s return policy protects both buyers and sellers:\n\n⏰ **Return Window:**\n• 7 days from delivery for most items\n• 14 days for electronics and textbooks\n• 3 days for food and perishable items\n\n✅ **Return Conditions:**\n• Items must be unused and in original condition\n• Original packaging and tags required\n• Proof of purchase (order confirmation)\n• No damage caused by buyer\n\n🔄 **Return Process:**\n1. Go to "My Orders" and select the item\n2. Click "Request Return" and state reason\n3. Wait for seller approval\n4. Package item securely for return\n5. Use provided return label\n\n💰 **Refund Timeline:**\n• Refunds processed within 3-5 business days\n• Money returned to original payment method\n• Shipping costs may apply\n\n**Note:** Some items like custom orders or digital products may be non-returnable.'
  },
  
  // Selling Guide & Process
  {
    category: 'Selling Guide',
    questions: [
      'how to sell items',
      'become a seller',
      'list an item',
      'sell on this app',
      'how to post a product',
      'how to start selling',
      'seller guide',
      'selling process'
    ],
    answer: 'Start selling on UniTrade and reach thousands of university students:\n\n🚀 **Getting Started:**\n• Upgrade to Seller account in your profile\n• Complete seller verification process\n• Set up your seller profile and bio\n• Add payment and banking details\n\n📸 **Listing Products:**\n1. Click "Add Product" in seller dashboard\n2. Upload high-quality photos (up to 8 images)\n3. Write detailed product description\n4. Set competitive pricing\n5. Choose category and add tags\n6. Set inventory quantity\n7. Publish your listing\n\n📊 **Seller Dashboard Features:**\n• Track sales and revenue\n• Manage inventory and orders\n• View customer messages\n• Access analytics and insights\n• Bulk upload products\n• Promotional tools\n\n💡 **Selling Tips:**\n• Use clear, well-lit photos\n• Write honest, detailed descriptions\n• Price competitively\n• Respond quickly to messages\n• Maintain good seller ratings'
  },

  // Payment Methods & Security
  {
    category: 'Payments & Security',
    questions: [
      'payment methods',
      'how to pay',
      'what payment options',
      'mobile money',
      'credit card',
      'bank transfer',
      'payment security',
      'safe payments'
    ],
    answer: 'UniTrade offers secure payment options for all Ghanaian users:\n\n📱 **Mobile Money:**\n• MTN Mobile Money\n• Vodafone Cash\n• AirtelTigo Money\n• All major mobile money providers\n\n💳 **Card Payments:**\n• Visa and Mastercard\n• Local and international cards\n• Secure 3D authentication\n• Instant payment processing\n\n🏦 **Bank Transfers:**\n• Direct bank transfers\n• Online banking integration\n• All major Ghanaian banks supported\n\n🔒 **Security Features:**\n• End-to-end encryption\n• PCI DSS compliance\n• Fraud detection systems\n• Secure payment gateways\n• No card details stored\n• Two-factor authentication\n\n💰 **Payment Protection:**\n• Escrow service for high-value items\n• Buyer protection guarantee\n• Dispute resolution system\n• Refund protection'
  },

  // Delivery & Shipping
  {
    category: 'Delivery & Shipping',
    questions: [
      'how long does delivery take',
      'delivery time',
      'when will i get my order',
      'shipping time',
      'how long for delivery',
      'when will it arrive',
      'delivery options',
      'shipping methods'
    ],
    answer: 'UniTrade offers flexible delivery options across Ghana:\n\n🚚 **Delivery Methods:**\n• **Campus Pickup:** Free pickup from seller\'s university (0-1 days)\n• **Local Delivery:** Same city/region delivery (1-2 days)\n• **National Shipping:** Nationwide delivery (2-5 days)\n• **Express Delivery:** Fast nationwide shipping (1-3 days, extra fee)\n\n📍 **Coverage Areas:**\n• All university campuses in Ghana\n• Major cities: Accra, Kumasi, Tamale, Cape Coast\n• Regional capitals and towns\n• Remote areas (may take longer)\n\n💰 **Delivery Costs:**\n• Campus pickup: FREE\n• Local delivery: GHS 5-15\n• National shipping: GHS 10-30\n• Express delivery: GHS 20-50\n\n📦 **Delivery Features:**\n• Real-time tracking\n• SMS/email notifications\n• Secure packaging\n• Proof of delivery\n• Insurance for valuable items'
  },

  // Support & Help
  {
    category: 'Support & Help',
    questions: [
      'customer support',
      'help center',
      'contact support',
      'need help',
      'technical support',
      'report problem',
      'complaint',
      'issue with order'
    ],
    answer: 'UniTrade support is here to help you 24/7:\n\n💬 **Get Help:**\n• **AI Chat Assistant:** Instant help right here!\n• **Live Chat:** Connect with human agents\n• **Email Support:** support@unitrade.com.gh\n• **Phone Support:** +233 XX XXX XXXX\n• **WhatsApp:** Available for urgent issues\n\n🎓 **University Support:**\n• Campus representatives at major universities\n• Student ambassador program\n• On-campus pickup points\n• University-specific help\n\n📚 **Self-Help Resources:**\n• Comprehensive FAQ section\n• Video tutorials and guides\n• Community forums\n• Seller best practices\n\n⚡ **Response Times:**\n• AI Chat: Instant\n• Live Chat: Under 5 minutes\n• Email: Within 24 hours\n• Phone: Business hours (8 AM - 8 PM)\n\n🔧 **Common Issues We Help With:**\n• Account and login problems\n• Payment and billing questions\n• Order and delivery issues\n• Technical difficulties\n• Seller verification\n• Dispute resolution'
  },

  // Mobile App & Features
  {
    category: 'Mobile App',
    questions: [
      'mobile app',
      'download app',
      'app features',
      'mobile version',
      'smartphone app',
      'ios android app'
    ],
    answer: 'UniTrade\'s mobile app brings the full marketplace experience to your phone:\n\n📱 **App Features:**\n• **Responsive Design:** Works perfectly on all devices\n• **Push Notifications:** Real-time order and message alerts\n• **Camera Integration:** Easy photo uploads for listings\n• **Location Services:** Find nearby sellers and pickup points\n• **Offline Mode:** Browse cached content without internet\n\n🔔 **Smart Notifications:**\n• New messages from buyers/sellers\n• Order status updates\n• Price drop alerts on wishlist items\n• New products in your categories\n• Payment confirmations\n\n📸 **Mobile-Optimized Features:**\n• Quick photo capture for listings\n• Barcode scanner for textbooks\n• Voice search functionality\n• Swipe gestures for easy navigation\n• Touch-friendly interface\n\n💾 **Access Options:**\n• **Web App:** Works on any mobile browser\n• **Progressive Web App (PWA):** Install like a native app\n• **Responsive Design:** Optimized for all screen sizes\n\n**Note:** Our web app is fully mobile-optimized and works like a native app!'
  },
  
  // Payments & Orders
  {
    category: 'Payments & Orders',
    questions: [
      'what payment methods do you accept',
      'how can i pay',
      'payment options',
      'do you accept mobile money',
      'can i pay with card',
      'payment methods'
    ],
    answer: 'We accept mobile money (MTN, Vodafone, AirtelTigo), bank transfers, and debit/credit cards (Visa, Mastercard).'
  },
  {
    category: 'Payments & Orders',
    questions: [
      'how can i track my order',
      'track order',
      'where is my order',
      'order status',
      'track delivery',
      'order tracking'
    ],
    answer: 'You\'ll receive an SMS or email with a tracking link after your order is shipped. You can also check your order status in the "My Orders" section of your account.'
  },
  
  // Returns & Support
  {
    category: 'Returns & Support',
    questions: [
      'can i return a product',
      'return policy',
      'how to return',
      'can i get a refund',
      'return item',
      'return process'
    ],
    answer: 'Absolutely! You can request a return within 7 days of receiving your order, provided the item is unused, in its original packaging, and with all tags attached. Some items may be excluded. Please see our Returns Policy for full details.'
  },
  {
    category: 'Returns & Support',
    questions: [
      'i have an issue with my order',
      'problem with order',
      'my order is wrong',
      'missing item',
      'damaged item',
      'help with order'
    ],
    answer: 'I\'m here to help. Please describe the issue so I can assist you further or connect you to our support team.'
  },
  
  // Greetings & Welcome
  {
    category: 'Greetings',
    questions: [
      'hello',
      'hi',
      'hey',
      'good morning',
      'good afternoon',
      'good evening',
      'greetings'
    ],
    answer: '👋 Hello! Welcome to UniTrade - Ghana\'s #1 University Marketplace!\n\n🎓 **Proudly developed by Nexus Coders from UENR**\n\nI\'m your AI assistant, ready to help with:\n• 🛍️ Shopping and buying questions\n• 📦 Selling and listing products\n• 🎓 University-specific information\n• 💳 Payments and security\n• 🚚 Delivery and shipping\n• 🔧 Technical support\n• 👨‍💻 Information about our development team\n\nWhat can I help you with today?'
  },
  {
    category: 'Gratitude',
    questions: [
      'thank you',
      'thanks',
      'appreciate it',
      'that helps',
      'thank you for your help',
      'thanks a lot'
    ],
    answer: '🙏 You\'re very welcome! I\'m glad I could help.\n\nIs there anything else about UniTrade I can assist you with? I\'m here 24/7 to help with:\n• Account questions\n• Product inquiries\n• Order support\n• Technical issues\n\nFeel free to ask anytime! 😊'
  },

  // Farewell
  {
    category: 'Farewell',
    questions: [
      'goodbye',
      'bye',
      'see you later',
      'talk to you later',
      'have a good day',
      'farewell'
    ],
    answer: '👋 Goodbye! Thank you for using UniTrade!\n\nRemember:\n• 🛍️ Browse thousands of products from your university community\n• 📱 Enjoy our mobile-optimized experience\n• 💬 I\'m always here if you need help\n• 🎓 Happy studying and trading!\n\nHave a wonderful day! 🌟'
  },
  
  // Fallback for unknown questions
  {
    category: 'General',
    questions: [],
    answer: '🤔 I\'m not sure I understand that question. Let me help you find what you\'re looking for!\n\n**Popular Topics:**\n• 🛍️ **Shopping:** "How to buy products"\n• 📦 **Selling:** "How to sell items"\n• 🎓 **Universities:** "Which universities do you support"\n• 💳 **Payments:** "What payment methods"\n• 🚚 **Delivery:** "How long does delivery take"\n• 👤 **Account:** "How to create account"\n\n**Need Human Help?**\nType "contact support" to speak with our team, or try rephrasing your question. I\'m here to help! 😊'
  }
];

// Function to find the best matching answer for a user's question
export function findAnswer(userQuestion) {
  if (!userQuestion || userQuestion.trim() === '') {
    return "I'm here to help! What would you like to know?";
  }

  // Record this question for frequency tracking
  recordQuestionFrequency(userQuestion);

  // Convert to lowercase for case-insensitive matching
  const query = userQuestion.toLowerCase().trim();
  
  // Common greetings and farewells
  const greetings = ['hi', 'hello', 'hey', 'greetings', 'good morning', 'good afternoon', 'good evening'];
  const farewells = ['bye', 'goodbye', 'see you', 'thanks', 'thank you', 'thank you very much'];
  
  // Handle greetings
  if (greetings.some(word => query.includes(word))) {
    const greetingResponses = [
      "👋 Hello! Welcome to UniTrade - Ghana's premier university marketplace! How can I help you today?",
      "🎓 Hi there! I'm your UniTrade assistant. What can I help you with?",
      "🌟 Greetings! Ready to explore UniTrade? What would you like to know?",
      "😊 Hello! I'm here to help with all your UniTrade questions. What's on your mind?"
    ];
    return greetingResponses[Math.floor(Math.random() * greetingResponses.length)];
  }
  
  // Handle farewells
  if (farewells.some(word => query.includes(word))) {
    const farewellResponses = [
      "🙏 You're welcome! Thanks for using UniTrade. Feel free to ask anytime - I'm here 24/7! Have a great day! 🌟",
      "😊 Happy to help! Come back whenever you need assistance with UniTrade. Goodbye and happy trading! 🛍️",
      "👋 Thank you for choosing UniTrade! Have a wonderful day and enjoy your university marketplace experience! 🎓",
      "🌟 You're very welcome! Don't hesitate to return if you need more help. Happy studying and trading! 📚"
    ];
    return farewellResponses[Math.floor(Math.random() * farewellResponses.length)];
  }
  
  // Check for exact matches first
  for (const item of knowledgeBase) {
    if (item.questions.some(q => query === q.toLowerCase())) {
      return item.answer;
    }
  }
  
  // Then check for close matches with high confidence
  for (const item of knowledgeBase) {
    if (item.questions.some(q => {
      const question = q.toLowerCase();
      // Check if query contains the entire question or vice versa
      return query.includes(question) || question.includes(query);
    })) {
      return item.answer;
    }
  }
  
  // Check for location-specific questions
  const locationMatch = query.match(/deliver to (\w+)/i) || 
                       query.match(/ship to (\w+)/i) ||
                       query.match(/do you deliver in (\w+)/i);
  
  if (locationMatch) {
    const location = locationMatch[1];
    return `We currently deliver to most locations. For specific delivery options to ${location}, please check the shipping information during checkout or contact our support team.`;
  }
  
  // Check for price-related questions
  if (query.includes('price') || query.includes('cost') || query.includes('how much')) {
    return "Product prices are set by individual sellers. You can see the price on each product page. Some items may have special offers or discounts applied at checkout.";
  }
  
  // Check for payment method questions
  if (query.includes('payment method') || query.includes('how to pay') || query.includes('credit card')) {
    return "We accept various payment methods including:\n• Credit/Debit Cards (Visa, Mastercard, etc.)\n• Mobile Money\n• Bank Transfers\n• Other local payment options\n\nAll payments are processed securely.";
  }
  
  // Finally, check for any keyword matches with scoring
  const queryWords = query.split(' ').filter(word => word.length > 3);
  const matchedItems = [];
  
  for (const item of knowledgeBase) {
    const matchCount = item.questions.reduce((count, q) => {
      const questionWords = q.toLowerCase().split(' ');
      return count + questionWords.filter(word => 
        word.length > 3 && queryWords.includes(word)
      ).length;
    }, 0);
    
    if (matchCount > 0) {
      matchedItems.push({ item, score: matchCount });
    }
  }
  
  if (matchedItems.length > 0) {
    // Return the best match if confidence is high enough
    matchedItems.sort((a, b) => b.score - a.score);
    if (matchedItems[0].score >= 2) {
      return matchedItems[0].item.answer;
    }
  }
  
  // If no good matches found, record as unanswered and provide helpful response
  recordUnansweredQuestion(userQuestion, { 
    timestamp: new Date().toISOString(),
    queryProcessed: query,
    matchAttempts: matchedItems.length 
  });

  // Get suggested similar questions
  const suggestions = getSuggestedQuestions(userQuestion, 3);
  
  let fallbackResponse = "🤔 I'm still learning about that topic! Let me help you find what you need:\n\n";
  
  if (suggestions.length > 0) {
    fallbackResponse += "**Did you mean:**\n";
    suggestions.forEach((suggestion, index) => {
      fallbackResponse += `• "${suggestion}"\n`;
    });
    fallbackResponse += "\n";
  }
  
  fallbackResponse += "**Popular Topics:**\n";
  fallbackResponse += "• 🛍️ **Shopping:** \"How to buy products\"\n";
  fallbackResponse += "• 📦 **Selling:** \"How to sell items\"\n";
  fallbackResponse += "• 🎓 **Universities:** \"Which universities do you support\"\n";
  fallbackResponse += "• 💳 **Payments:** \"What payment methods\"\n";
  fallbackResponse += "• 🚚 **Delivery:** \"How long does delivery take\"\n";
  fallbackResponse += "• 👤 **Account:** \"How to create account\"\n";
  fallbackResponse += "• 👨‍💻 **Developers:** \"Who developed UniTrade\"\n\n";
  fallbackResponse += "**Need Human Help?** Type \"contact support\" to speak with our team! 😊";
  
  return fallbackResponse;
}
